import React from 'react';
import { useAppContext } from '../../../../context/app-context';
import { useFeatureFlags } from '../../../../hooks/use-feature-flags';
import { AlignmentControl } from '../../alignment-control';
import { FontFamilyControl } from '../../font-family-control';
import { FontSizeControl } from '../../font-size-control';
import { ColorTab } from '../../font-drawer/fragments/color-tab';
import { PositionTab } from '../../font-drawer/fragments/position-tab';
import { FontToolbarInlineTool } from '../font-toolbar-types';
import { useFontToolbarCanvas } from '../hooks/use-font-toolbar-canvas';
import styles from './font-inline-tools.module.scss';

type FontInlineToolsProps = {
  tool: FontToolbarInlineTool;
};

/**
 * Renders the correct standalone control for the active FontToolbar inline
 * tool. Each control manages its own preview state and writes back to the
 * canvas via its onChange / canvas ref.
 *
 * All controls are wrapped in a fixed 402px container to keep the toolbar
 * width consistent regardless of which tool is active.
 */
export const FontInlineTools = ({ tool }: FontInlineToolsProps): React.ReactElement | null => {
  const {
    appState: { isMobileApp },
  } = useAppContext();
  const { IS_MOBILE_COLOR_SCROLL_ENABLED } = useFeatureFlags();
  const {
    fonts,
    initialFontFamily,
    initialFontSize,
    initialAlignment,
    onFontFamilyChange,
    onFontSizeChange,
    onAlignmentChange,
  } = useFontToolbarCanvas();

  const renderTool = () => {
    switch (tool) {
      case 'font-family':
        return (
          <FontFamilyControl
            fonts={fonts}
            initialFontFamily={initialFontFamily}
            onChange={onFontFamilyChange}
            label="Font"
            dropUp={Boolean(isMobileApp)}
          />
        );
      case 'font-size':
        return (
          <FontSizeControl
            initialFontSize={initialFontSize}
            onChange={onFontSizeChange}
            label="Size"
            showLabel={false}
          />
        );
      case 'alignment':
        return (
          <AlignmentControl
            initialAlignment={initialAlignment}
            onChange={onAlignmentChange}
            label="Alignment"
            showLabel={false}
          />
        );
      case 'color':
        return <ColorTab scrollable={isMobileApp === true && IS_MOBILE_COLOR_SCROLL_ENABLED} showLabel={false} />;
      case 'position':
        return <PositionTab />;
      default:
        return null;
    }
  };

  const isMobile = Boolean(isMobileApp);
  const containerClass = [styles['font-inline-tools'], isMobile && styles['font-inline-tools--mobile']]
    .filter(Boolean)
    .join(' ');

  const content = renderTool();
  if (!content) return null;

  return <div className={containerClass}>{content}</div>;
};
